// ADR-0003 D4–D9 sync manager. Pure orchestration over a `Transport` — no
// network, no SDK imports, no `import.meta.env`. Tests pass a fake
// transport and a fake clock; production wires the real PocketBase
// transport from `./pocketbase.ts`.
//
// T-025 fixes (from Fable's audit):
//   DEF-1  create() sends the client id (no destructuring in the transport).
//   DEF-2  update-then-delete emits a delete, not an update.
//   DEF-3  pulled rows keep the real server owner; createWorkout throws when
//          no owner is set.
//   DEF-4  LWW compares client clocks; the cursor advances on server time;
//          the pull path never calls transport.* directly.
//   DEF-5  cancelled groups are removed from the queue.
//   DEF-6  4xx is permanent (T-014 F2 — PATCH 404 is a rule rejection).
//   DEF-7  push only `pending`; `failed` entries are surfaced via
//          failedEntries()/retry(id)/discard(id) and protect the row from
//          being clobbered by a pull.

import {
  getDb,
  getOwner,
  onLocalWrite,
  type SyncQueueEntry,
  type SyncOp,
  type SyncedRow,
} from "./db";
import type { Table } from "dexie";
import type { ServerRecord, Transport } from "./pocketbase";

function localTable(collection: string): Table<SyncedRow, string> {
  const db = getDb();
  if (collection === "log_entries") {
    return db.log_entries as unknown as Table<SyncedRow, string>;
  }
  if (collection === "workout_logs") {
    return db.workout_logs as unknown as Table<SyncedRow, string>;
  }
  if (collection === "progress_photos") {
    return db.progress_photos as unknown as Table<SyncedRow, string>;
  }
  // v1 fallback: old queue entries may still reference the `workouts` table.
  return db.workouts as unknown as Table<SyncedRow, string>;
}

export type SyncStatus =
  | "idle"
  | "syncing"
  | "offline"
  | "auth-required"
  | "failed";

export interface SyncManagerDeps {
  transport: Transport;
  /** Anything with `nowIso`, `setTimeout`, `clearTimeout`, `advance` for tests. */
  clock: Clock;
  collections: string[];
  /** Debounce window for the local-write trigger (D8). */
  debounceMs?: number;
  /** Backoff base in ms; default 1000 (D7). */
  baseBackoffMs?: number;
  /** Max attempts before marking an entry `failed`; default 5. */
  maxAttempts?: number;
}

export interface Clock {
  nowIso(): string;
  setTimeout(cb: () => void, ms: number): unknown;
  clearTimeout(handle: unknown): void;
  /** Move the clock forward and fire any due timers. */
  advance(ms: number): void;
}

export class SyncManager {
  readonly #deps: Required<SyncManagerDeps>;
  #inFlight: Promise<void> | null = null;
  #rerun: boolean = false;
  #debounceHandle: unknown = null;
  #status: SyncStatus = "idle";
  #unsubscribe: () => void;
  /** Backoff base, exposed for tests so they can speed up retries. */
  baseBackoffMs: number;

  constructor(deps: SyncManagerDeps) {
    this.#deps = {
      debounceMs: 2000,
      baseBackoffMs: 1000,
      maxAttempts: 5,
      ...deps,
    };
    this.baseBackoffMs = this.#deps.baseBackoffMs;
    this.#unsubscribe = onLocalWrite(() => this.#onLocalWrite());
  }

  get status(): SyncStatus {
    return this.#status;
  }

  /**
   * ADR-0003 D5: at most one sync in flight. Local writes that land
   * *during* a run set a rerun flag; the run loops once more after
   * finishing if anything was enqueued.
   */
  syncNow(): Promise<void> {
    if (this.#inFlight) {
      this.#rerun = true;
      return this.#inFlight;
    }
    const p = this.#run().finally(() => {
      this.#inFlight = null;
    });
    this.#inFlight = p;
    return p;
  }

  /** Public so tests / shutdown can detach the local-write trigger. */
  dispose(): void {
    this.#unsubscribe();
  }

  // --- D7 surface API ----------------------------------------------------

  /** All entries currently in `failed` state, with payloads preserved. */
  async failedEntries(): Promise<SyncQueueEntry[]> {
    return getDb().syncQueue.where("status").equals("failed").toArray();
  }

  /** Re-arm a `failed` entry so the next sync will try it again. */
  async retry(entryId: string): Promise<void> {
    await getDb().syncQueue
      .where("id")
      .equals(entryId)
      .modify((e: SyncQueueEntry) => {
        e.status = "pending";
        e.attempts = 0;
        e.lastError = undefined;
      });
  }

  /**
   * Permanently remove a `failed` entry. If the op was `create`, the
   * local row is also removed (the server never saw it; the user has
   * decided to drop it). For `update` and `delete`, the local row is
   * kept — the next pull will reconcile it with the server copy.
   *
   *  ADR-0011 errata F-4: if the entry was a photo create, the
   *  `photoBlobs` row is deleted in the same transaction so the
   *  bytes do not become an orphan that the LRU trim cannot reach. */
  async discard(entryId: string): Promise<void> {
    const entry = await getDb().syncQueue.get(entryId);
    if (!entry) return;
    const db = getDb();
    await db.transaction("rw", [db.syncQueue, db.progress_photos, db.photoBlobs], async () => {
      await db.syncQueue.delete(entryId);
      if (entry.op === "create") {
        if (entry.collection === "progress_photos" && entry.blobKey) {
          // Photo create: drop both the local row AND the in-flight
          // blob. Leaving the blob would make it a permanent
          // orphan (F-4).
          await db.progress_photos.delete(entry.recordId).catch(() => undefined);
          await db.photoBlobs.delete(entry.recordId).catch(() => undefined);
        } else {
          await localTable(entry.collection).delete(entry.recordId).catch(() => undefined);
        }
      }
    });
  }

  // --- internal ---------------------------------------------------------

  #onLocalWrite(): void {
    this.#deps.clock.clearTimeout(this.#debounceHandle);
    this.#debounceHandle = this.#deps.clock.setTimeout(() => {
      this.#debounceHandle = null;
      void this.syncNow();
    }, this.#deps.debounceMs);
  }

  async #run(): Promise<void> {
    // Track failure across the rerun loop so a "failed" status is not
    // silently reset to "idle" by a no-op follow-up run (e.g. a debounce
    // timer firing after the run completed). The user must keep seeing
    // the failure until a subsequent sync actually succeeds.
    let anyFailedAcrossRuns = false;
    let prevStatus: SyncStatus | null = null;
    do {
      this.#rerun = false;
      prevStatus = this.#status;
      this.#status = "syncing";
      let anyFailed = false;
      let networkErr = false;
      try {
        for (const collection of this.#deps.collections) {
          const pushFailed = await this.#pushCollection(collection);
          if (pushFailed) anyFailed = true;
          try {
            await this.#pullCollection(collection);
          } catch (err) {
            if (isNetworkError(err)) {
              networkErr = true;
            } else {
              throw err;
            }
          }
        }
        if (networkErr) {
          this.#status = "offline";
        } else if (anyFailed) {
          this.#status = "failed";
        } else if (anyFailedAcrossRuns) {
          // No new work this iteration, and a prior iteration failed:
          // keep the previous status (typically "failed") visible.
          this.#status = prevStatus ?? "failed";
        } else {
          this.#status = "idle";
        }
        anyFailedAcrossRuns = anyFailedAcrossRuns || anyFailed;
      } catch (err) {
        if (is401(err)) {
          this.#status = "auth-required";
          anyFailedAcrossRuns = true;
          return; // queue untouched (D9)
        }
        if (isNetworkError(err)) {
          this.#status = "offline";
        } else {
          this.#status = "failed";
        }
        anyFailedAcrossRuns = true;
      }
    } while (this.#rerun);
  }

  // ---- PUSH ---------------------------------------------------------------

  async #pushCollection(collection: string): Promise<boolean> {
    // DEF-7: only `pending` entries are pushed. `failed` entries are
    // surfaced via the retry/discard API and protect the row from a
    // clobbering pull.
    const queue = await getDb().syncQueue
      .where("[collection+status]")
      .equals([collection, "pending"])
      .toArray();
    if (queue.length === 0) return false;

    // Coalesce per recordId using the state fold (DEF-2/DEF-5).
    const groups = coalesceByRecordId(queue);
    let failed = false;
    for (const group of groups) {
      // T-097 §4 (ADR-0003 rev 2 barrier): a held `create` for the
      // same recordId in this collection blocks this `pending`
      // group. Sending an update or delete ahead of its create is a
      // 404 the superseded probe turns into a permanent failure. No
      // writer produces this shape today (held is only ever a
      // `workout_logs` create, and `logSet` flips it to pending in
      // the same transaction as the first log_entries create), but
      // the guard is required so a future writer cannot park an
      // entry as a 404. The held entry stays in the queue and is
      // pushed by a later sync cycle once `logSet` flips it.
      const heldBarrier = await getDb().syncQueue
        .where("[collection+status]")
        .equals([collection, "held"])
        .filter((q) => q.recordId === group.recordId)
        .first();
      if (heldBarrier) continue;
      // DEF-5: a cancelled group must be removed from the queue.
      if (group.cancel) {
        const ids = queue
          .filter((q) => q.recordId === group.recordId)
          .map((q) => q.id);
        const db = getDb();
        // ADR-0011 errata F-4: a `create + delete` cancel group for
        // a photo leaves a `photoBlobs` row with `uploaded: false`
        // and no entry — an orphan the LRU trim cannot reach because
        // it requires `uploaded === true`. Drop the blob and the
        // local row in the same transaction.
        if (collection === "progress_photos") {
          await db.transaction("rw", [db.syncQueue, db.progress_photos, db.photoBlobs], async () => {
            await db.syncQueue.bulkDelete(ids);
            await db.progress_photos.delete(group.recordId).catch(() => undefined);
            await db.photoBlobs.delete(group.recordId).catch(() => undefined);
          });
        } else {
          await db.syncQueue.bulkDelete(ids);
        }
        continue;
      }
      if (group.op === "create" || group.op === "update") {
        const ok = await this.#sendPayload(collection, group.recordId, group.op, group.payload, queue, group);
        if (!ok) failed = true;
      } else if (group.op === "delete") {
        const ok = await this.#sendDelete(collection, group.recordId, queue);
        if (!ok) failed = true;
      }
    }
    return failed;
  }

  async #sendPayload(
    collection: string,
    recordId: string,
    op: "create" | "update",
    payloadJson: string,
    queue: SyncQueueEntry[],
    group: CoalescedGroup,
  ): Promise<boolean> {
    const parsed = JSON.parse(payloadJson) as ServerRecord;
    // ADR-0011 D2 — `photo` (and any future file field on this
    // collection) never rides a JSON create or update. `queueEntry()`
    // already strips it when the row becomes an entry, but a caller
    // that hand-writes the queue could leave the field in; defence in
    // depth at the egress costs nothing and keeps the LWW rule safe.
    if (collection === "progress_photos") {
      delete (parsed as Record<string, unknown>).photo;
    }
    const record = parsed;
    const entryIds = queue.filter((q) => q.recordId === recordId).map((q) => q.id);
    if (group.blobKey && op === "create") {
      // ADR-0011 D2 + D3 — the multipart / idempotent path.
      const ok = await this.#sendBlobCreate(collection, record, entryIds, group.blobKey, group.fileField ?? "photo");
      return ok;
    }
    const ok = await this.#withRetries(
      entryIds,
      async () => {
        if (op === "create") {
          try {
            await this.#deps.transport.create(collection, record);
            return;
          } catch (err) {
            // T-090 F-1: the server has no upsert. A second `create`
            // for the same `(owner, exercise)` on `user_progress` is
            // a permanent 400 (unique index). Branch on the response
            // SHAPE — a 400 whose body carries `data.owner` or
            // `data.exercise` — not the literal
            // `validation_not_unique` string; the deployed
            // PocketBase version is not exposed to us and codes
            // have drifted before (2026-08-24).
            if (
              collection === "user_progress" &&
              isDuplicateUserProgress400(err, record)
            ) {
              const adopted = await this.#adoptDuplicateUserProgress(
                collection,
                record,
              );
              if (adopted) return; // recovery succeeded
              throw err; // recovery failed — let the retry classifier park it
            }
            throw err;
          }
        } else {
          await this.#deps.transport.update(collection, recordId, record);
        }
      },
      op === "update"
        ? { collection, recordId, payloadUpdatedAt: record.updatedAt }
        : undefined,
    );
    if (ok) await getDb().syncQueue.bulkDelete(entryIds);
    return ok;
  }

  /** ADR-0011 D2 + D3 — multipart create with idempotent retry.
   *
   *  The client id is the idempotency key. If a previous attempt
   *  landed on the server (a 5xx / timeout / cancelled fetch after the
   *  create completed), PocketBase returns 400 with the field-level
   *  error `validation_not_unique` on `id`. We then `get` the record,
   *  inspect its `photo` field, and either accept the upload (file is
   *  there) or run a single `updateWithFiles` to attach it.
   *
   *  Transient errors (0, 5xx, 429) retry with backoff. Permanent 4xx
   *  (including 413 from nginx) marks the entry failed and keeps the
   *  blob so the user can retry or discard.
   *
   *  Success closes three things in one Dexie transaction: the outbox
   *  entries are deleted, the photoBlobs row's `uploaded` flag is
   *  flipped, and the local progress_photos row learns the stored
   *  filename.
   */
  async #sendBlobCreate(
    collection: string,
    record: ServerRecord,
    entryIds: string[],
    blobKey: string,
    fileField: string,
  ): Promise<boolean> {
    const db = getDb();
    // ADR-0011 D2 + D3. The Blob lives in the `photoBlobs` Dexie row;
    // we read it once here and reuse it across retries so the bytes
    // are never lost. The Dexie row is the source of truth — the
    // previous in-process cache was removed in the F-5 fix.
    const blobRow = await db.photoBlobs.get(blobKey);
    const blob = blobRow?.blob;
    if (!blob || typeof blob.size !== "number") {
      // The blob is gone (eviction, manual clear, test isolation).
      // The queue entry is orphaned; mark it failed so the user can
      // see the problem.
      await db.syncQueue.where("id").anyOf(entryIds).modify(
        (e: SyncQueueEntry) => {
          e.status = "failed";
          e.lastError = "photo.blob.missing";
        },
      );
      return false;
    }

    // F-6: the retry / classify loop runs in its own method so the
    // probe-recovery branch can re-enter it after handling the
    // duplicate-id case. The loop never throws; transient errors
    // return to the caller for the engine's outer retry budget, and
    // permanent errors mark `failed`.
    let attempt = 0;
    while (true) {
      let err: unknown;
      try {
        const created = await this.#deps.transport.createWithFiles(
          collection,
          record,
          { [fileField]: blob },
        );
        await this.#commitBlobSuccess(entryIds, blobKey, fileField, created);
        return true;
      } catch (caught) {
        err = caught;
      }
      if (is401(err)) throw err;
      // ADR-0011 D3 step 2 + errata E-1 / F-2 fix. PocketBase 0.23
      // reports a duplicate client id on create as
      // `data.id.code === "validation_pk_invalid"` (NOT
      // `validation_not_unique`, which is the unique-INDEX case
      // `authErrors.ts` already handles for `users.email`). The
      // robust contract is "any 400 on the blob create path":
      // probe with `get`, and if the row exists, either accept the
      // file or send one `updateWithFiles` as a defensive repair.
      // This also catches F-7 (a retry racing a still-committing
      // first request that fails at the DB layer with a generic
      // 400 and no `data.id`).
      if (looksLikePermanent400(err)) {
        try {
          const recovered = await this.#probeAndRepair(
            collection,
            record,
            blobKey,
            fileField,
            blob,
            entryIds,
            attempt + 1,
          );
          if (recovered === "success") return true;
          if (recovered === "failed") return false;
          // recovered === "retry": the probe/repair saw a transient
          // error — fall through and let the loop's classifier
          // bump attempts and back off.
          attempt += 1;
          if (attempt >= this.#deps.maxAttempts) {
            await db.syncQueue.where("id").anyOf(entryIds).modify(
              (e: SyncQueueEntry) => {
                e.status = "failed";
                e.attempts = attempt;
                e.lastError = "photo.blob.max-attempts";
              },
            );
            return false;
          }
          const delay = backoffMs(attempt, this.baseBackoffMs);
          await sleep(this.#deps.clock, delay);
          continue;
        } catch (probeErr) {
          // Defensive: the probe/repair helper classifies internally
          // and never throws, but if a non-network exception escapes
          // (programmer error, storage quota, etc.) we still classify
          // it the same way as the create error.
          err = probeErr;
        }
      }
      if (isPermanentFailure(err)) {
        attempt += 1;
        await db.syncQueue.where("id").anyOf(entryIds).modify(
          (e: SyncQueueEntry) => {
            e.status = "failed";
            e.attempts = attempt;
            e.lastError = (err as Error).message;
          },
        );
        return false;
      }
      attempt += 1;
      if (attempt >= this.#deps.maxAttempts) {
        await db.syncQueue.where("id").anyOf(entryIds).modify(
          (e: SyncQueueEntry) => {
            e.status = "failed";
            e.attempts = attempt;
            e.lastError = (err as Error).message;
          },
        );
        return false;
      }
      await db.syncQueue.where("id").anyOf(entryIds).modify(
        (e: SyncQueueEntry) => {
          e.attempts = attempt;
          e.lastError = (err as Error).message;
        },
      );
      const delay = backoffMs(attempt, this.baseBackoffMs);
      await sleep(this.#deps.clock, delay);
    }
  }

  /** F-6: probe the server for an existing row + file, and run the
   *  one `updateWithFiles` repair if the row is there without a file.
   *  Returns a tri-state so the outer loop can drive retries:
   *    "success" — the row is on the server with its file; commit.
   *    "failed"  — the row is on the server but cannot be repaired
   *                 (file still empty after one `updateWithFiles`).
   *    "retry"   — a transient error happened during the probe or
   *                 repair; the outer loop should back off and try
   *                 the create again. */
  async #probeAndRepair(
    collection: string,
    record: ServerRecord,
    blobKey: string,
    fileField: string,
    blob: Blob,
    entryIds: string[],
    attemptForMark: number,
  ): Promise<"success" | "failed" | "retry"> {
    const db = getDb();
    let remote: ServerRecord | null = null;
    try {
      remote = await this.#deps.transport.get(collection, record.id);
    } catch (err) {
      if (is401(err)) throw err;
      if (isPermanentFailure(err)) {
        // Probe itself rejected as a rule miss (cross-user id
        // collision, etc.). Mark failed; do NOT repair.
        await db.syncQueue.where("id").anyOf(entryIds).modify(
          (e: SyncQueueEntry) => {
            e.status = "failed";
            e.attempts = attemptForMark;
            e.lastError = (err as Error).message;
          },
        );
        return "failed";
      }
      // Transient — retry.
      return "retry";
    }
    const remotePhoto =
      remote && (remote[fileField] as string | undefined);
    if (remote && typeof remotePhoto === "string" && remotePhoto.length > 0) {
      // The server already has both the row and the file.
      const normalised: ServerRecord = { ...remote, photo: remotePhoto };
      await this.#commitBlobSuccess(entryIds, blobKey, fileField, normalised);
      return "success";
    }
    // Row landed but the file did not (truncated multipart —
    // defensive; per errata E-3 this cannot occur on PocketBase).
    try {
      const recovered = await this.#deps.transport.updateWithFiles(
        collection,
        record.id,
        record,
        { [fileField]: blob },
      );
      if (recovered && (recovered[fileField] as string | undefined)) {
        await this.#commitBlobSuccess(entryIds, blobKey, fileField, recovered);
        return "success";
      }
    } catch (err) {
      if (is401(err)) throw err;
      if (isPermanentFailure(err)) {
        await db.syncQueue.where("id").anyOf(entryIds).modify(
          (e: SyncQueueEntry) => {
            e.status = "failed";
            e.attempts = attemptForMark;
            e.lastError = (err as Error).message;
          },
        );
        return "failed";
      }
      return "retry";
    }
    // Still empty after one repair — give up. ADR-0011 D3 step 2
    // last bullet.
    await db.syncQueue.where("id").anyOf(entryIds).modify(
      (e: SyncQueueEntry) => {
        e.status = "failed";
        e.attempts = attemptForMark;
        e.lastError = "photo.blob.never-stored";
      },
    );
    return "failed";
  }

  /** The closing transaction of a successful photo upload. Three
   *  writes, one transaction: delete the queue entries, mark the blob
   *  uploaded, write the server filename back to the local row. */
  async #commitBlobSuccess(
    entryIds: string[],
    blobKey: string,
    fileField: string,
    created: ServerRecord,
  ): Promise<void> {
    const db = getDb();
    const storedFilename = (created as Record<string, unknown>)[fileField] as string | undefined;
    await db.transaction(
      "rw",
      [db.syncQueue, db.photoBlobs, db.progress_photos],
      async () => {
        await db.syncQueue.bulkDelete(entryIds);
        const blob = await db.photoBlobs.get(blobKey);
        if (blob) {
          await db.photoBlobs.put({
            ...blob,
            uploaded: true,
            lastUsedAt: new Date().toISOString(),
          });
        }
        const localRow = await db.progress_photos.get(blobKey);
        if (localRow) {
          await db.progress_photos.put({
            ...localRow,
            photo: storedFilename ?? localRow.photo,
            updatedAt: created.updatedAt ?? localRow.updatedAt,
          });
        }
      },
    );
  }

  async #sendDelete(
    collection: string,
    recordId: string,
    queue: SyncQueueEntry[]
  ): Promise<boolean> {
    const local = await localTable(collection).get(recordId).catch(() => undefined);
    const updatedAt = (local as SyncedRow | undefined)?.updatedAt ?? this.#deps.clock.nowIso();
    const entryIds = queue.filter((q) => q.recordId === recordId).map((q) => q.id);
    const ok = await this.#withRetries(
      entryIds,
      async () => {
        await this.#deps.transport.softDelete(collection, recordId, updatedAt);
      },
      { collection, recordId, payloadUpdatedAt: updatedAt },
    );
    if (ok) await getDb().syncQueue.bulkDelete(entryIds);
    return ok;
  }

  async #withRetries(
    entryIds: string[],
    op: () => Promise<void>,
    context?: { collection: string; recordId: string; payloadUpdatedAt: string },
  ): Promise<boolean> {
    let attempt = 0;
    // eslint-disable-next-line no-constant-condition
    while (true) {
      try {
        await op();
        return true;
      } catch (err) {
        if (is401(err)) throw err;

        // Gate 0 D3: stale-write guard. A 404 on update/softDelete may mean
        // the server row is newer, not missing. Probe with transport.get and,
        // if the server record exists and is newer, drop the queue entry as
        // superseded instead of failing it.
        if (
          isPermanentFailure(err) &&
          context &&
          (err as { status?: number }).status === 404
        ) {
          const remote = await this.#deps.transport.get(
            context.collection,
            context.recordId,
          );
          if (remote) {
            const remoteTime = Date.parse(remote.updatedAt);
            const localTime = Date.parse(context.payloadUpdatedAt);
            if (
              !Number.isNaN(remoteTime) &&
              !Number.isNaN(localTime) &&
              remoteTime > localTime
            ) {
              await getDb().syncQueue.bulkDelete(entryIds);
              return true; // superseded — next pull reconciles
            }
          }
        }

        if (isPermanentFailure(err)) {
          attempt += 1;
          await getDb().syncQueue
            .where("id")
            .anyOf(entryIds)
            .modify((e: SyncQueueEntry) => {
              e.status = "failed";
              e.attempts = attempt;
              e.lastError = (err as Error).message;
            });
          return false;
        }
        attempt += 1;
        if (attempt >= this.#deps.maxAttempts) {
          await getDb().syncQueue
            .where("id")
            .anyOf(entryIds)
            .modify((e: SyncQueueEntry) => {
              e.status = "failed";
              e.attempts = attempt;
              e.lastError = (err as Error).message;
            });
          return false; // surfaced, not dropped
        }
        await getDb().syncQueue
          .where("id")
          .anyOf(entryIds)
          .modify((e: SyncQueueEntry) => {
            e.attempts = attempt;
            e.lastError = (err as Error).message;
          });
        const delay = backoffMs(attempt, this.baseBackoffMs);
        await sleep(this.#deps.clock, delay);
      }
    }
  }

  // ---- PULL ---------------------------------------------------------------

  async #pullCollection(collection: string): Promise<void> {
    const meta = await getDb().syncMeta.get(collection);
    const cursor = meta?.cursor ?? "";
    const remote = await this.#deps.transport.listUpdatedSince(collection, cursor);
    if (remote.length === 0) return;

    // DEF-4: the cursor advances on server time (`serverUpdated`), not
    // on the client clock stored as `updatedAt`. We track the max
    // seen here and only persist it after the batch is applied. If
    // `serverUpdated` is missing for any reason, fall back to
    // `updatedAt` so the cursor still advances and we don't refetch
    // the same row forever.
    let maxServerUpdated = cursor;
    for (const r of remote) {
      const s = r.serverUpdated || r.updatedAt;
      if (s > maxServerUpdated) maxServerUpdated = s;
    }

    for (const r of remote) {
      await this.#applyRemote(collection, r);
    }

    await getDb().syncMeta.put({ collection, cursor: maxServerUpdated });
  }

  async #applyRemote(_collection: string, remote: ServerRecord): Promise<void> {
    // ADR-0003 D3 + D5: never overwrite a local record that has a
    // pending or `failed` queue entry. Pending: the user has an edit
    // in flight that will overwrite the server copy. Failed: the
    // user has not given up on the edit (they may retry); overwriting
    // it under a `failed` would silently lose their change.
    const blocking = await getDb().syncQueue
      .where("recordId")
      .equals(remote.id)
      .filter(
        (e: SyncQueueEntry) => e.status === "pending" || e.status === "failed"
      )
      .first();
    if (blocking) return;

    const local = (await localTable(_collection).get(remote.id)) as SyncedRow | undefined;
    if (!local) {
      // Brand-new record from the server. DEF-3: keep the server's
      // owner, not the literal "remote".
      if (remote.deletedAt) {
        await localTable(_collection).put({
          id: remote.id,
          owner: (remote.owner as string) || "remote",
          updatedAt: remote.updatedAt,
          deletedAt: remote.deletedAt,
        } as SyncedRow);
      } else {
        await localTable(_collection).put({
          ...remote,
          owner: (remote.owner as string) || "remote",
        } as unknown as SyncedRow);
      }
      return;
    }

    if (remote.deletedAt) {
      // Tombstone: mark locally, never physically delete. (D2)
      await localTable(_collection).put({
        ...local,
        deletedAt: remote.deletedAt,
        updatedAt: remote.updatedAt,
      } as SyncedRow);
      return;
    }

    // DEF-4 + T-035 F-1: LWW is on the client clock (`updatedAt`), and
    // both forms (ISO `T` and PocketBase's normalised space form) are
    // parsed to a number before comparison so string ordering cannot invert
    // the result.
    const remoteTime = Date.parse(remote.updatedAt);
    const localTime = Date.parse(local.updatedAt);
    if (!Number.isNaN(remoteTime) && !Number.isNaN(localTime) && remoteTime > localTime) {
      await localTable(_collection).put({
        ...local,
        ...remote,
        owner: local.owner,
        deletedAt: null,
      } as SyncedRow);
    }
    // equal or local-newer: no-op.
  }

  /** T-090 F-1: adopt the server's id for an existing `user_progress`
   *  row, then re-emit as an `update`. The runner's pre-check covers
   *  the common case (local row exists → emit update); this is the
   *  recovery for when the local row is missing but the server has
   *  one (lost local row, second device, or eviction).
   *
   *  Patches the local user_progress row's id to the server's so
   *  subsequent writes (a second `finishWorkout` tomorrow, or a
   *  tombstone) hit the same row. The stale-write guard (rev 5,
   *  live 2026-09-06) requires `updatedAt` ≥ the server's value,
   *  so we set it to `now` which is naturally > any prior server
   *  value. */
  async #adoptDuplicateUserProgress(
    collection: string,
    record: Record<string, unknown>,
  ): Promise<boolean> {
    const exercise = String(record.exercise ?? "");
    if (!exercise) return false;
    // R-25 (orchestrator, 2026-09-06): the list rule allows a coach
    // to read paired athletes' rows. We must filter on the owner
    // before selecting — otherwise the recovery can adopt a
    // FOREIGN id and rewrite the local table to point at somebody
    // else's record.
    const ownerId = String(record.owner ?? getOwner() ?? "");
    if (!ownerId) return false;
    let rows: { id: string; owner?: string; [k: string]: unknown }[];
    try {
      rows = await this.#deps.transport.listByExercise(
        collection,
        ownerId,
        exercise,
      );
    } catch {
      return false;
    }
    // Select the row whose owner is THIS authenticated owner, even
    // if the list returned others. If no such row came back, do NOT
    // adopt anything — let the entry fail visibly rather than point
    // the local table at a stranger's id.
    const own = rows.find((r) => String(r.owner ?? "") === ownerId);
    if (!own) return false;
    const serverRow = own;
    const adoptedId = String(serverRow.id);
    const localId = String(record.id ?? "");
    if (!adoptedId) return false;
    const updatedAt = new Date().toISOString();
    const rewritten = { ...record, id: adoptedId, updatedAt };
    const db = getDb();
    // T-090 R-25 H4 idempotency: when the listed row's id already
    // equals the payload id, the previous adopt already happened
    // (a retry re-entered after `op` was flipped to "update", or a
    // duplicate-create recovery ran twice). Skip the local rewrite
    // — the entry is already an `update` against the adopted id —
    // and just issue the update so the queue drains.
    if (adoptedId === localId) {
      await this.#deps.transport.update(collection, adoptedId, rewritten as never);
      await db.syncQueue
        .where("recordId")
        .equals(adoptedId)
        .and(
          (e: SyncQueueEntry) =>
            e.collection === collection && e.op === "create",
        )
        .delete();
      return true;
    }
    await db.transaction("rw", [db.user_progress, db.syncQueue], async () => {
      const local = await db.user_progress.get(localId);
      if (local) {
        await db.user_progress.put({
          ...local,
          id: adoptedId,
          updatedAt,
          ...(rewritten as Record<string, unknown>),
        } as never);
        if (localId !== adoptedId) {
          // The original local id never existed on the server (the
          // create was refused). Tombstone it locally so the engine
          // doesn't re-emit it on the next push.
          await db.user_progress.update(localId, { deletedAt: updatedAt });
        }
      }
      // T-090 R-25 H4: the entry's `op` was "create"; if the process
      // dies between this transaction and `transport.update` (or if
      // `update` throws once and the retry re-enters through
      // `create`), the next push sends a *create* with the server's
      // id → 400 (`data.id` + the unique index on `(owner, exercise)`)
      // → adoption again → `adoptedId === localId` → false → thrown →
      // permanent, parked. Flip op to "update" in the SAME modify so
      // the crash window emits an update when the engine picks up.
      await db.syncQueue
        .where("recordId")
        .equals(localId)
        .modify((e: SyncQueueEntry) => {
          e.recordId = adoptedId;
          e.op = "update";
          e.payload = JSON.stringify(rewritten);
        });
    });
    await this.#deps.transport.update(collection, adoptedId, rewritten as never);
    await db.syncQueue
      .where("recordId")
      .equals(adoptedId)
      .and(
        (e: SyncQueueEntry) =>
          e.collection === collection && e.op === "create",
      )
      .delete();
    return true;
  }
}

interface CoalescedGroup {
  recordId: string;
  op: SyncOp;
  payload: string;
  cancel: boolean;
  /** ADR-0011 D1/D2: present only when the coalesced group includes
   *  at least one entry with a `blobKey`. Equals `recordId`. */
  blobKey?: string;
  /** ADR-0011 D2: server field name, e.g. "photo". */
  fileField?: string;
}

/**
 * Fable's state fold (T-025 DEF-2/DEF-5).
 *
 *   create+…+delete  → cancel (the server never saw the create)
 *   update+delete     → delete
 *   delete            → delete
 *   create+update…    → create with the last payload
 *   update+update…    → update with the last payload
 *
 * Implemented as a state machine: track the first op's class (create
 * vs update) and walk the entries in `createdAt` order, folding the
 * latest payload in. A trailing `delete` flips the result to
 * `delete`, and cancels only if we ever saw a `create`.
 */
function coalesceByRecordId(entries: SyncQueueEntry[]): CoalescedGroup[] {
  const sorted = [...entries].sort((a, b) =>
    a.createdAt.localeCompare(b.createdAt)
  );
  const map = new Map<string, SyncQueueEntry[]>();
  for (const e of sorted) {
    const arr = map.get(e.recordId);
    if (arr) arr.push(e);
    else map.set(e.recordId, [e]);
  }
  const result: CoalescedGroup[] = [];
  for (const [recordId, group] of map) {
    let op: "create" | "update" | "delete" | "cancel" | null = null;
    let lastPayload = "";
    let blobKey: string | undefined;
    let fileField: string | undefined;
    for (const e of group) {
      if (e.blobKey) {
        blobKey = e.blobKey;
        fileField = e.fileField ?? fileField;
      }
      if (e.op === "create") {
        op = "create";
        lastPayload = e.payload;
      } else if (e.op === "update") {
        // A trailing update does not downgrade a create.
        if (op !== "create") op = "update";
        lastPayload = e.payload;
      } else {
        // delete
        op = op === "create" ? "cancel" : "delete";
      }
    }
    if (op === "cancel") {
      result.push({ recordId, op: "delete", payload: "{}", cancel: true });
    } else if (op === "delete") {
      result.push({ recordId, op: "delete", payload: "{}", cancel: false });
    } else if (op === "create") {
      result.push({
        recordId,
        op: "create",
        payload: lastPayload,
        cancel: false,
        blobKey,
        fileField,
      });
    } else {
      result.push({
        recordId,
        op: "update",
        payload: lastPayload,
        cancel: false,
        blobKey,
        fileField,
      });
    }
  }
  return result;
}

function backoffMs(attempt: number, base: number): number {
  // Exponential with full jitter: random delay in [0, base * 2^(attempt-1)).
  const cap = base * 2 ** (attempt - 1);
  return Math.floor(Math.random() * cap);
}

function sleep(clock: Clock, ms: number): Promise<void> {
  return new Promise((resolve) => {
    clock.setTimeout(resolve, ms);
  });
}

function is401(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const status = (err as { status?: number }).status;
  return status === 401;
}

/**
 * DEF-6: HTTP status codes that are not transient. 4xx in general
 * (T-014 F2 — PocketBase update rule rejection returns 404). Only 5xx,
 * 0/undefined (network), and 429 retry with backoff.
 */
function isPermanentFailure(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  const status = (err as { status?: number }).status;
  if (typeof status !== "number") return false;
  if (status === 429) return false; // too many requests — retry
  return status >= 400 && status < 500;
}

/** Network-level error (no status, fetch failed, etc.). Not permanent. */
function isNetworkError(err: unknown): boolean {
  if (!err || typeof err !== "object") return false;
  if ((err as { status?: number }).status !== undefined) return false;
  const msg = String((err as { message?: string }).message ?? err).toLowerCase();
  return /network|fetch|offline|timeout|econnrefused|enotfound/.test(msg);
}

/** ADR-0011 errata E-1 / F-2 fix. A duplicate client id on create
 *  is `data.id.code === "validation_pk_invalid"` on PocketBase 0.23,
 *  not `validation_not_unique` (that is the unique-INDEX case for
 *  ordinary fields like `users.email`, handled by `authErrors.ts`).
 *  The robust contract is "any 400 on the blob create path": a 400
 *  on a multipart create with our client id either means the row
 *  already exists (the previous attempt landed) or means some other
 *  rule rejected us. We probe with `get` either way; the server's
 *  state tells us which. */
function looksLikePermanent400(err: unknown): boolean {
  if (!isPermanentFailure(err)) return false;
  // Only the 4xx-without-network shape is interesting: 0/5xx/429 are
  // transient and handled by the outer classifier. 4xx is permanent.
  return (err as { status?: number }).status === 400;
}

/** T-090 F-1: a 400 whose body carries `data.owner` or
 *  `data.exercise` is the unique-index refusal on
 *  `(owner, exercise)`. We do NOT key on the literal string
 *  `validation_not_unique` — codes have drifted before. */
function isDuplicateUserProgress400(
  err: unknown,
  record: Record<string, unknown>,
): boolean {
  if (!looksLikePermanent400(err)) return false;
  const data = (err as { data?: Record<string, unknown> }).data;
  if (!data || typeof data !== "object") return false;
  // Field-level errors PocketBase 0.23 puts under `data.<field>`.
  if (data.owner || data.exercise) return true;
  // Body-level shape (some deployments wrap differently).
  const owner = String(record.owner ?? "");
  const exercise = String(record.exercise ?? "");
  if (owner && data[owner]) return true;
  if (exercise && data[exercise]) return true;
  return false;
}
